import React, { useEffect } from 'react';
import { useTranslations } from 'hooks/useTranslations';
import { Grid, Typography } from '@mui/material';
import { useConfigurationLicence } from '../hooks';
import { FormModal } from 'components/molecules/Modal';
import { Resolver, useForm } from 'react-hook-form';
import { ILicenceTemplateApplicationProcessTaskForm } from 'models/licenceConfigurator';
import { yupResolver } from '@hookform/resolvers/yup';
import { colors } from 'theme/colors';
import { FormInput } from 'components/molecules/FormInput';
import useTaskFormSchema from './schemaValidation';
import { useAlert } from 'models/alertContext';
import { ActionTypes } from 'state/actions/alert';
import { Alert, EAlertVariants } from 'components/atoms/Alert';
import { FormSelect } from 'components/molecules/FormSelect';
import { mapDictionaryToOptionProp } from 'helpers/dictionary';
import { useLang } from 'models/langContext';
import { EDictionaryTypes } from 'models/dictionary';

interface ILicenceTemplateApplicationProcessAddTask {
  handleClose: () => void;
  addSuccess: () => void;
  id: string;
}

const initialFormValues = {
  nameUniqueId: '',
  description: '',
};

const LicenceTemplateApplicationProcessAddTask = ({
  handleClose,
  addSuccess,
  id,
}: ILicenceTemplateApplicationProcessAddTask) => {
  const { t } = useTranslations();
  const { selectedLanguage } = useLang();
  const { addApplicationProccessTask, loadDictionaries, task } = useConfigurationLicence();
  const { schema } = useTaskFormSchema();
  const { clearAlert, taskAlert } = useAlert();

  useEffect(() => {
    (async function initDictionaries() {
      await loadDictionaries([EDictionaryTypes.task]);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);

  const close = () => {
    reset(initialFormValues);
    if (taskAlert && taskAlert.variant === EAlertVariants.error) {
      clearAlert(ActionTypes.CLEAR_TASK_ALERT);
    }
    handleClose();
  };

  useEffect(
    () => {
      clearAlert(ActionTypes.CLEAR_TASK_ALERT);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
    reset,
  } = useForm<ILicenceTemplateApplicationProcessTaskForm>({
    defaultValues: initialFormValues,
    resolver: yupResolver(
      schema,
    ) as unknown as Resolver<ILicenceTemplateApplicationProcessTaskForm>,
  });

  const handleAdd = async (data: ILicenceTemplateApplicationProcessTaskForm) => {
    const res = await addApplicationProccessTask({ ...data, applicationProcessId: id });
    if (res) {
      addSuccess();
      close();
    }
  };

  return (
    <form onSubmit={handleSubmit(handleAdd)}>
      <FormModal
        open={true}
        onClickClose={() => {
          close();
          reset(initialFormValues);
        }}
        onClickApprove={handleSubmit(handleAdd)}
        approveButtonLabel={t('general.save.button')}
      >
        <Grid sx={{ gap: '4px' }}>
          <Grid sx={{ gap: 1.5, flexDirection: 'column', mb: 2.5 }}>
            <Typography variant="h5" sx={{ color: colors.primary500 }}>
              {t('licence.addTask.label')}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <FormSelect
              options={mapDictionaryToOptionProp(task)}
              setValue={setValue}
              name="nameUniqueId"
              label={t('licence.addTask.name.label')}
              control={control}
              errors={errors}
              required
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <FormInput
              name="description"
              label={t('licence.addTask.description.label')}
              multiline
              setValue={setValue}
              minRows={6}
              control={control}
              errors={errors}
              required
              charsLimit={255}
              showCounter
            />
          </Grid>
          {taskAlert && taskAlert.variant === EAlertVariants.error && (
            <Grid container size={{ xs: 12 }}>
              <Alert text={taskAlert.text} variant={taskAlert.variant} />
            </Grid>
          )}
        </Grid>
      </FormModal>
    </form>
  );
};

export { LicenceTemplateApplicationProcessAddTask };
